import styled from 'styled-components';
import fonts from '../../ui/fonts';
import { colors } from '../../ui/design-tokens';
import { getSectionGradient, media, space, rems } from '../../ui/helpers';
import { MAIN_MENU_HEIGHT, SUB_MENU_HEIGHT, CONTACT_MENU } from '../../ui/settings';

const Container = styled.div<{ full: boolean; gradient: boolean; secondMenu: boolean }>`
  background: ${({ theme, gradient }) => (gradient ? getSectionGradient(theme) : theme.background)};
  color: ${({ theme }) => theme.color};
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  padding-top: ${({ secondMenu }) => (secondMenu ? space(15) : rems(MAIN_MENU_HEIGHT))};
  height: ${({ full }) => (full ? `calc(100vh - ${rems(SUB_MENU_HEIGHT)})` : 'auto')};

  @media (max-height: 720px) {
    padding-top: ${({ secondMenu }) => (secondMenu ? `60px` : 0)};
  }

  .bottomDecoration {
    bottom: 0;
    left: 0;
    max-width: 220vw;
    pointer-events: none;
    position: absolute;
    z-index: 2;
  }

  .franchisesBottom {
    background: ${({ theme }) => (theme?.bar ? theme?.bar : colors.neutral[100])};
    bottom: 0;
    left: 0;
    height: ${space(4)};
    opacity: 0.7;
    pointer-events: none;
    position: absolute;
    width: 100%;
    z-index: 2;
  }

  ${media.min('xsmall')`
    .franchisesBottom {
      height: ${space(6)};
    }
  `}

  ${media.min('small')`
    .franchisesBottom {
      height: ${space(8)};
    }
  `}

  ${media.min('medium')`
    height: ${({ full }) => (full ? `calc(100vh - ${rems(SUB_MENU_HEIGHT)})` : '70vh')};
    padding-top: 0;
  `}
`;

const Content = styled.div<{ full: boolean }>`
  display: flex;
  flex-direction: ${({ full }) => (full ? `column` : `column-reverse`)};
  padding-top: ${({ full }) => (full ? `0` : `${space(4)}`)};
  height: ${({ full }) =>
    full ? `calc(100vh - ${rems(SUB_MENU_HEIGHT)} - ${rems(CONTACT_MENU)})` : 'auto'};
  justify-content: ${({ full }) => (full ? `flex-start` : 'initial')};

  @media (max-height: 720px) {
    ${({ full }) => (full ? `justify-content: flex-start` : '')};
    padding-top: ${space(10)};
    height: ${({ full }) => (full ? `60%` : '')};
  }

  ${media.min('medium')`
    flex-direction: row;
    height: ${({ full }) => (full ? `calc(100vh - ${rems(SUB_MENU_HEIGHT)})` : '100%')};
    overflow: hidden;
    flex-direction: row-reverse;
    padding-top: ${space(10)};
  `}

  .containerCta {
    align-items: center;
    flex-direction: column;
    display: ${({ full }) => (full ? `flex` : 'none')};
    position: absolute;
    bottom: ${space(6)};
    left: 0;
    width: 100%;

    ${media.min('medium')`
      bottom: 0;
    `}

    @media (max-height: 720px) {
      display: none;
    }

    .cta {
      color: ${colors.neutral[100]};
      border: 2px solid ${colors.neutral[100]};
      border-radius: 50%;
      height: ${rems(30)};
      margin-bottom: ${space(1)};
      width: ${rems(30)};
      line-height: 0;

      &:hover {
        background: ${colors.neutral[200]};
        border: 2px solid ${colors.neutral[200]};
        .svg {
          path {
            fill: ${colors.primary[600]};
          }
        }
      }

      .svg {
        height: ${rems(8)};
        width: ${rems(12)};

        path {
          fill: ${colors.neutral[100]};
        }
      }
    }
  }
`;

const Small = styled.div`
  height: initial;
  padding: ${space(10)} 0;
  text-align: center;
  position: relative;

  ${media.min('medium')`
    padding: ${space(13)} 0 ${space(8)};
    min-height: 332px;
  `}

  .claim {
    ${fonts.highlightsMD};
    padding-bottom: ${space(2)};
  }

  .title {
    padding-bottom: ${space(3)};
  }
`;

const TextZone = styled.div<{ full: boolean }>`
  text-align: center;
  padding: 0 ${space(3)};
  padding-top: ${({ full }) => (full ? `${space(3)}` : '0')};

  .title {
    margin-left: -3px;
    margin-bottom: 20px; // margen debajo de la imagen en modo movil
  }

  .claim {
    ${fonts.highlightsMD};
    padding-bottom: ${space(3)};
  }

  .ctaContainer {
    margin-top: ${space(4)};
  }

  ${media.min('small')`
    text-align: center;
  `}

  ${media.min('medium')`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0;
    text-align: left;
    width: 55%;

    .title {
      margin: ${space(3)} 0;
      margin-left: -3px;
      padding-top: 0;
      margin-top: 0;
    }

    .claim {
      ${fonts.highlightsMD};

    ${({ size }) =>
      size === 'billboard-personalizado' &&
    `
      font-size: 0.8rem;  // Ajusta el tamaño deseado aquí
      font-weight: bold;
    `
    }

      
    }

    .ctaContainer {
      margin-top: ${space(3)};
      margin-bottom: ${space(3)};

      a:hover {
        background: ${colors.neutral[200]};
        border-color:  ${colors.neutral[200]};
      }
    }
  `}

  ${media.min('large')`
    width: 55%;
  `}
`;

const ImageZone = styled.div<{ ratio: number; full: boolean }>`
  margin: 0 auto 0 auto;
  width: 100%;
  height: 60%;

  @media (max-height: 720px) {
    height: 90%;
  }

  .image {
    width: 100%;
    height: 100%;
    img {
      left: 50%;
      margin: 0 auto;
      height: 100%;
      max-width: initial;
      transform: translateX(-50%);
      width: auto;
    }
  }

  ${media.min('medium')`
    margin: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    height: 100%;
    width: 45%;

    .image {
      aspect-ratio: ${({ ratio }) => ratio && ratio};
      width: auto;
      height: auto;

      img {
        width: auto;
      }
    }
  `}

  ${media.min('large')`
    width: 45%;
  `}
`;

const Image = styled.div<{ bg: string }>`
  background-image: ${({ bg }) => `url(${bg})`};
  background-repeat: repeat-x;
  height: 69px;
  overflow: hidden;
  position: absolute;
  background-position-y: bottom;
  width: 100%;
  bottom: 0;
`;

export { Container, Content, Small, TextZone, ImageZone, Image };
